// Strings for release 3 (0019): municipality coordinator, cross-school
// authorisation, municipal and period reports, What works, school calendar
// and early signals, the staff notice and own check-in, the head staff view,
// THL alignment, the published method and question set validation.
// Merged into STRINGS in strings.ts. Finnish and Swedish are drafts and need
// native-speaker review.

type Shape<T> = { [K in keyof T]: T[K] extends (...a: infer A) => string ? (...a: A) => string : string };
const n = (c: number, one: string, many: string) => (c === 1 ? one : many);

export const r3En = {
  'r3.role.coordinator': 'Municipality coordinator',
  'r3.role.coordinatorDesc': 'Sees the municipality’s quarterly wellbeing picture, pooled across the schools that agreed to take part. Never one school, never a class.',

  'r3.nav.whatWorks': 'What works',
  'r3.nav.staff': 'Staff and students',
  'r3.nav.calendar': 'Calendar',

  // shared
  'r3.schoolsN': (c: number) => n(c, '1 school', `${c} schools`),
  'r3.tooFewSchools': (c: number) => `Too few schools (${c})`,
  'r3.st.ok': 'Shown',
  'r3.st.too_few_schools': 'Too few schools',
  'r3.st.insufficient': 'Too few answers to show',
  'r3.st.insufficient_spread': 'Hidden — one school would show through',
  'r3.st.suppressed_near_unanimous': 'Hidden — nearly all the same',
  'r3.st.hidden_complement': 'Hidden with another stage',
  'r3.stage.primary': 'Grades 1–6',
  'r3.stage.lower_secondary': 'Grades 7–9',
  'r3.stage.upper_secondary': 'Upper secondary',
  'r3.term.spring': (y: string) => `Spring term ${y}`,
  'r3.term.autumn': (y: string) => `Autumn term ${y}`,
  'r3.notCausal': 'This shows what happened next, not that the activity caused it.',
  'r3.evidence': (band: string, n2: number, mood: string) => `Across ${band} schools (${n2} times tried), the next check-in’s mood changed by ${mood}.`,
  'r3.evidenceBase': (b: string) => `Where nothing was tried: ${b}.`,
  'r3.premiumWw': 'What works is part of the premium insights option. Ask Zencera to switch it on for your school.',
  'r3.premiumSignals': 'Early signals are part of the premium insights option.',

  // staff notice (I5)
  'r3.notice.title': 'Before you start: your information',
  'r3.notice.intro': 'Please read this once. It says what Swipe stores about you as a member of staff, who can see what, and how to have it removed.',
  'r3.notice.storedTitle': 'What we store about you',
  'r3.notice.stored': 'Your name, email address and the roles you hold in each school; when you sign in; and a log of the changes you make (for example starting a check-in or approving access). No grades, no assessments of your work.',
  'r3.notice.whoTitle': 'Who sees it',
  'r3.notice.who': 'Your school admin and head of school see your name and roles. The activity log is visible to the head and the school admin. Zencera support sees it only in support mode, which the school can see in its log.',
  'r3.notice.studentsTitle': 'Students’ answers',
  'r3.notice.students': 'Students answer without names. You see your own class only when at least five answered. Nobody, including you, sees a single student.',
  'r3.notice.checkinTitle': 'Your own check-in (optional)',
  'r3.notice.checkin': 'You can check in on your own workload and energy. It is private to you and needs a separate consent. Your first check-in each month joins an anonymous school picture that the head sees only when at least five colleagues took part.',
  'r3.notice.removeTitle': 'Having it removed',
  'r3.notice.remove': 'Ask your school admin to remove you from the school, or ask Zencera to erase your account. Erasure removes your personal data; the activity log keeps only that an erased person did something.',
  'r3.notice.keepTitle': 'How long we keep it',
  'r3.notice.keep': 'Your account while you work at the school. Your own check-ins for 13 months, or until you withdraw consent. The activity log for the period agreed with the school.',
  'r3.notice.more': 'More detail:',
  'r3.notice.trustLink': 'Trust and compliance documents',
  'r3.notice.accept': 'I have read this',

  // own check-in (I5/I6)
  'r3.me.nav': 'My check-in',
  'r3.me.title': 'My check-in',
  'r3.me.consentTitle': 'Your own check-in',
  'r3.me.c1': 'Four questions, 1–5: workload, energy, sleep and stress. Takes a minute.',
  'r3.me.c2': 'Your answers are private: only you see them. Not your head, not your school admin, not Zencera.',
  'r3.me.c3': 'Your first check-in each month also joins an anonymous school picture, stored without your name or the time. The head sees it only when at least five colleagues took part; heads’ own answers are never in it.',
  'r3.me.c4': 'You can withdraw at any time: your history is deleted at once. Anonymous monthly totals already added can’t be traced back to you and stay.',
  'r3.me.agree': 'I agree to my own check-in on these terms.',
  'r3.me.consent': 'Start my check-in',
  'r3.me.consentGiven': 'Consent recorded. You can check in now.',
  'r3.me.private': 'Only you see these answers.',
  'r3.me.counted': 'This month’s anonymous school picture already includes you.',
  'r3.me.notCounted': 'Your first check-in this month joins the anonymous school picture.',
  'r3.me.todayTitle': 'How are you today?',
  'r3.me.todayDone': 'Today’s check-in (you can change it)',
  'r3.me.save': 'Save',
  'r3.me.saved': 'Saved.',
  'r3.me.history': 'Your recent check-ins',
  'r3.me.noHistory': 'Nothing yet.',
  'r3.me.day': 'Day',
  'r3.me.keep': 'Kept for 13 months, then deleted.',
  'r3.me.withdraw': 'Withdraw consent and delete my history',
  'r3.me.withdrawTitle': 'Withdraw consent?',
  'r3.me.withdrawBody': 'Your check-in history is deleted now and can’t be restored. Anonymous monthly totals already added stay, as they can’t be linked to you.',
  'r3.me.withdrawConfirm': 'Withdraw and delete',
  'r3.me.withdrawn': 'Consent withdrawn. Your history is deleted.',
  'r3.dim.workload': 'Workload',
  'r3.dim.energy': 'Energy',
  'r3.dim.sleep': 'Sleep',
  'r3.dim.stress': 'Stress',
  'r3.dimHint.workload': '1 = very light, 5 = far too much',
  'r3.dimHint.energy': '1 = exhausted, 5 = full of energy',
  'r3.dimHint.sleep': '1 = very poorly, 5 = very well',
  'r3.dimHint.stress': '1 = calm, 5 = very stressed',
  'r3.me.pickedOf': (v: number) => `${v} of 5`,

  // head: staff and students (J8)
  'r3.sv.title': 'Staff and students',
  'r3.sv.intro': (f: number) => `Monthly, side by side: students’ mood and the staff’s own anonymous check-ins. A staff figure shows only when at least ${f} colleagues answered and not nearly all the same. Heads’ own answers are never included.`,
  'r3.sv.tableTitle': 'Complete months',
  'r3.sv.month': 'Month',
  'r3.sv.students': 'Students’ mood',
  'r3.sv.scale': 'Staff scales: workload and stress — higher is heavier; energy and sleep — higher is better.',
  'r3.sv.never': 'Never by person, team or subject. This month appears when it is over.',
  'r3.sv.wholeOnly': 'The staff view is for whole-school heads.',

  // head: What works (J3)
  'r3.ww.title': 'What works',
  'r3.ww.term': (term: string) => `What works · ${term}`,
  'r3.ww.how': (s: number, a: number, r: number) => `Activities from the playbook, and how classes’ next check-in compared. Shown only with at least ${s} schools, ${a} times tried, ${r} answers before and after, and no school more than half.`,
  'r3.ww.none': 'No activity had enough schools and answers this term.',
  'r3.ww.notYet': 'Not computed yet. Zencera runs it once a term has ended.',
  'r3.ww.country': 'Finland',
  'r3.ww.municipality': (m: string) => `Municipality ${m}`,
  'r3.ww.activity': 'Activity',
  'r3.ww.schools': 'Schools',
  'r3.ww.tried': 'Times tried',
  'r3.ww.mood': 'Mood before → next',
  'r3.ww.compare': 'Where nothing was tried',
  'r3.ww.topic': 'Topic share',
  'r3.ww.pts': (v: string) => `${v} points`,
  'r3.ww.compareHint': 'The comparison is classes that had the same topic and logged no activity.',
  'r3.ww.takesPart': 'Your school takes part in cross-school statistics.',
  'r3.ww.notTakingPart': 'Your school doesn’t take part in cross-school statistics yet. You can still see the results.',

  // calendar (J9)
  'r3.cal.title': 'School calendar',
  'r3.cal.intro': 'Exam weeks, holidays and events help explain a change in the results and help teachers plan check-ins. Teachers see the calendar; heads and the school admin edit it.',
  'r3.cal.add': 'Add to the calendar',
  'r3.cal.edit': 'Change the event',
  'r3.cal.kind': 'Kind',
  'r3.cal.k.exam_week': 'Exam week',
  'r3.cal.k.holiday': 'Holiday',
  'r3.cal.k.event': 'Event',
  'r3.cal.k.other': 'Other',
  'r3.cal.what': 'What it is',
  'r3.cal.noNames': 'Describe the week, not people: no names.',
  'r3.cal.from': 'From',
  'r3.cal.to': 'To',
  'r3.cal.gradesAll': 'Grades (none chosen = whole school)',
  'r3.cal.gradesYours': 'Your grades',
  'r3.cal.coming': 'In the calendar',
  'r3.cal.upcoming': 'Coming up at school',
  'r3.cal.none': 'Nothing in the calendar.',
  'r3.cal.wholeSchool': 'Whole school',
  'r3.cal.editBtn': 'Change',
  'r3.cal.deleteBtn': 'Delete',
  'r3.cal.deleted': 'Deleted.',

  // early signals (J9)
  'r3.sig.title': 'Early signals',
  'r3.sig.none': 'No topic is rising.',
  'r3.sig.rose': (s: string) => `rose for three weeks: ${s} % of answers`,
  'r3.sig.events': 'In the calendar those weeks',
  'r3.sig.how': 'A signal is a topic whose share rose in each of the last three complete weeks, by at least 10 points, using only weeks that were shown.',
  'r3.notif.signal': (topic: string) => `Early signal: “${topic}” has been rising for three weeks. Open School results.`,

  // THL (J5)
  'r3.thl.title': 'Compared with THL’s School Health Promotion study',
  'r3.thl.intro': 'The nearest indicator in THL’s national study for each of our topics, with reference values Zencera enters from THL’s published tables.',
  'r3.thl.mood': 'Mood',
  'r3.thl.noValue': 'no reference value entered yet',
  'r3.thl.note': 'The questions differ, so these are for context, not a direct comparison.',
  'r3.thlStage.4-5': 'Grades 4–5',
  'r3.thlStage.8-9': 'Grades 8–9',
  'r3.thlStage.lukio': 'Upper secondary (lukio)',
  'r3.thlStage.amm': 'Vocational (amm)',

  // municipality (J4)
  'r3.mu.title': 'Municipal wellbeing picture',
  'r3.mu.nav': 'Municipality',
  'r3.mu.intro': (m: string, a: number, min: number, per: number) => `${m}: ${n(a, '1 school has', `${a} schools have`)} agreed to take part. A quarter is shown when at least ${min} of them had ${per} or more answers, and never in a way that reveals one school.`,
  'r3.mu.quarters': 'Complete quarters',
  'r3.mu.quarter': 'Quarter',
  'r3.mu.all': 'All grades',
  'r3.mu.frozen': 'A quarter is computed once, after it ends, and never changes afterwards.',
  'r3.mu.topics': (p: string) => `What was on students’ minds · ${p}`,

  // platform admin (X, J3, J4, J5, J11)
  'r3.xs.title': 'Cross-school statistics',
  'r3.xs.hint': 'The school’s numbers join municipal and national statistics only after it has authorised it in writing. Only live Customer schools count. Withdrawing stops counting in anything computed afterwards.',
  'r3.xs.authorised': (on: string, who: string) => `Authorised ${on} · ${who}`,
  'r3.xs.notAuthorised': 'Not authorised',
  'r3.xs.notCounting': 'Not counted now: only live Customer schools count.',
  'r3.xs.withdraw': 'Record withdrawal',
  'r3.ai.nav': 'Insights',
  'r3.ai.title': 'Cross-school insights',
  'r3.ai.intro': (a: number, c: number, m: number) => `${n(a, '1 school takes', `${a} schools take`)} part. Zencera sees only pooled results: at least ${c} schools for Finland, ${m} for a municipality. Never one school.`,
  'r3.ai.byMuni': 'Taking part, by municipality',
  'r3.ai.noneYet': 'No school has authorised yet.',
  'r3.ai.coordN': (c: number) => n(c, '1 coordinator', `${c} coordinators`),
  'r3.ai.coordTitle': 'Municipality coordinators',
  'r3.ai.coordHint': 'A coordinator sees their municipality’s quarterly report only. They sign in with two-factor.',
  'r3.ai.email': 'Email of an existing account',
  'r3.ai.muni': 'Municipality',
  'r3.ai.choose': 'Choose…',
  'r3.ai.grant': 'Make coordinator',
  'r3.ai.revoke': 'Remove',
  'r3.ai.revoked': 'Coordinator removed.',
  'r3.ai.reportsTitle': 'Period reports',
  'r3.ai.reportsHint': 'Preview a complete quarter for Finland or a municipality. Published reports are public and labelled Draft.',
  'r3.ai.level': 'Area',
  'r3.ai.preview': 'Preview',
  'r3.ai.repTitle': 'Title',
  'r3.ai.repComment': 'Commentary (optional)',
  'r3.ai.publishHint': 'Publishing is refused if a reader could subtract it from another published report to see a small group.',
  'r3.ai.publish': 'Publish',
  'r3.ai.published': 'Published.',
  'r3.ai.publishedList': 'Published',
  'r3.ai.public': 'Public',
  'r3.ai.withdrawn': 'Withdrawn',
  'r3.ai.withdraw': 'Withdraw',
  'r3.ai.wwTitle': 'What works runs',
  'r3.ai.wwHint': 'Run once for each completed term. The result is frozen; nothing per school is kept.',
  'r3.ai.computedTerms': (s: string) => `Computed: ${s}`,
  'r3.ai.term': 'Term',
  'r3.ai.termHint': 'For example 2026-spring or 2026-autumn.',
  'r3.ai.compute': 'Compute',
  'r3.ai.computed': (c: number) => `Computed: ${n(c, '1 row', `${c} rows`)} cleared the floors.`,
  'r3.ai.thlHint': 'Enter values from THL’s published tables only, with the table as the source.',
  'r3.ai.indicator': 'Indicator',
  'r3.ai.stage': 'Grades',
  'r3.ai.year': 'Year',
  'r3.ai.value': 'Value (%)',
  'r3.ai.source': 'Source (THL table)',
  'r3.qv.title': 'Question set validation',
  'r3.qv.hint': 'Shown on the public method page. Mark a set validated only after review by a research partner.',
  'r3.qv.note': 'Note',
  'r3.qv.draft': 'Draft — not validated',
  'r3.qv.pilot_tested': 'Pilot-tested',
  'r3.qv.validated': 'Validated',

  // public: method (J6) and reports
  'r3.method.title': 'How Swipe protects privacy',
  'r3.method.intro': 'The rules below are the ones the database enforces. The numbers on this page are read from it.',
  'r3.method.failed': 'The figures could not be loaded. Try again later.',
  'r3.method.studentsTitle': 'Students',
  'r3.method.noId': 'Students answer without names, accounts or device identifiers. No free text.',
  'r3.method.floor': (f: number) => `A result is shown only when at least ${f} students answered.`,
  'r3.method.guard': 'If all but one answered the same, the result is hidden, so the one is not revealed.',
  'r3.method.complement': 'Whenever two shown results could be subtracted, the difference must also be large enough; otherwise one is hidden.',
  'r3.method.weeks': 'Heads of school see complete weeks only; a teacher sees their own class per finished check-in.',
  'r3.method.council': (f: number) => `The student council link shows school-level weeks with at least ${f} answers.`,
  'r3.method.staffTitle': 'Staff',
  'r3.method.staffPrivate': 'A staff member’s own check-in is private to them and needs their consent.',
  'r3.method.staffAgg': (f: number) => `The anonymous monthly staff picture is stored without names or times and shown to the head only with at least ${f} colleagues.`,
  'r3.method.staffHeads': 'Heads’ own answers are never in the picture a head sees.',
  'r3.method.xsTitle': 'Across schools',
  'r3.method.xsAuth': 'A school counts only after it has authorised it in writing.',
  'r3.method.xsFloors': (m: number, c: number, a: number) => `A municipality needs at least ${m} schools, Finland ${c}, each with ${a} or more answers; leaving out any one school must not reveal it.`,
  'r3.method.xsFrozen': 'Quarters and terms are computed once after they end and never change, so two readings can’t be compared to reveal a school.',
  'r3.method.ww': (a: number, r: number) => `“What works” needs ${a} times tried and ${r} answers before and after, with no school more than half.`,
  'r3.method.zencera': 'Zencera never sees an individual school’s results — only these pooled figures.',
  'r3.method.questionsTitle': 'Questions',
  'r3.pub.title': 'Published wellbeing reports',
  'r3.pub.intro': 'Pooled results across schools that agreed to take part. Draft until the method has been externally reviewed.',
  'r3.pub.none': 'No reports have been published yet.',

  // feature switches
  'fs.what_works': 'What works (cross-school)',
  'fs.municipality_reports': 'Municipal reports',
  'fs.period_reports': 'Zencera period reports',
  'fs.school_calendar': 'School calendar',
  'fs.early_signals': 'Early signals',
  'fs.staff_checkin': 'Staff own check-in',
  'fs.staff_view': 'Head staff view',
  'fs.thl_alignment': 'THL alignment',

  // errors
  'err.premium_required': 'This is part of the premium insights option.',
  'err.consent_required': 'Give your consent first.',
  'err.outside_scope': 'You can do this for your own grades only.',
  'err.whole_school_only': 'This is for whole-school heads.',
  'err.period_not_complete': 'Only complete periods can be shown.',
  'err.already_computed': 'This term has been computed and is frozen.',
  'err.already_published': 'This report is already published.',
  'err.below_floor': 'Too few schools or answers to publish.',
  'err.complement_too_small': 'Publishing this would let a small group be worked out by subtraction.',
  'err.already_coordinator': 'This person is already a coordinator.',
  'err.stale_notice': 'The notice has changed. Reload the page.',
  'err.not_found': 'Not found.',
  'err.unknown_account': 'No active account with that email.',
};

export type R3Strings = Shape<typeof r3En>;

export const r3Fi: R3Strings = {
  'r3.role.coordinator': 'Kunnan koordinaattori',
  'r3.role.coordinatorDesc': 'Näkee kunnan neljännesvuosittaisen hyvinvointikuvan osallistuvien koulujen yhdistettynä tuloksena. Ei koskaan yksittäistä koulua tai luokkaa.',
  'r3.nav.whatWorks': 'Mikä toimii',
  'r3.nav.staff': 'Henkilöstö ja oppilaat',
  'r3.nav.calendar': 'Kalenteri',
  'r3.schoolsN': (c) => n(c, '1 koulu', `${c} koulua`),
  'r3.tooFewSchools': (c) => `Liian vähän kouluja (${c})`,
  'r3.st.ok': 'Näytetään',
  'r3.st.too_few_schools': 'Liian vähän kouluja',
  'r3.st.insufficient': 'Liian vähän vastauksia näytettäväksi',
  'r3.st.insufficient_spread': 'Piilotettu – yksi koulu näkyisi',
  'r3.st.suppressed_near_unanimous': 'Piilotettu – lähes kaikki samaa',
  'r3.st.hidden_complement': 'Piilotettu toisen asteen kanssa',
  'r3.stage.primary': 'Vuosiluokat 1–6',
  'r3.stage.lower_secondary': 'Vuosiluokat 7–9',
  'r3.stage.upper_secondary': 'Toinen aste',
  'r3.term.spring': (y) => `Kevätlukukausi ${y}`,
  'r3.term.autumn': (y) => `Syyslukukausi ${y}`,
  'r3.notCausal': 'Tämä kertoo, mitä tapahtui seuraavaksi – ei sitä, että toiminta aiheutti sen.',
  'r3.evidence': (band, n2, mood) => `${band} koulussa (kokeiltu ${n2} kertaa) seuraavan kyselyn mieliala muuttui ${mood}.`,
  'r3.evidenceBase': (b) => `Kun mitään ei kokeiltu: ${b}.`,
  'r3.premiumWw': '”Mikä toimii” kuuluu laajennettuun oivallukset-lisäpalveluun. Pyydä Zenceraa ottamaan se käyttöön koulullesi.',
  'r3.premiumSignals': 'Varhaiset signaalit kuuluvat laajennettuun oivallukset-lisäpalveluun.',
  'r3.notice.title': 'Ennen kuin aloitat: tietosi',
  'r3.notice.intro': 'Lue tämä kerran. Tässä kerrotaan, mitä Swipe tallentaa sinusta henkilöstön jäsenenä, kuka näkee mitäkin ja miten tiedot poistetaan.',
  'r3.notice.storedTitle': 'Mitä sinusta tallennetaan',
  'r3.notice.stored': 'Nimesi, sähköpostiosoitteesi ja roolisi kussakin koulussa; kirjautumisajat; sekä loki tekemistäsi muutoksista (esimerkiksi kyselyn aloitus tai käyttöoikeuden hyväksyntä). Ei arvosanoja eikä työsi arviointia.',
  'r3.notice.whoTitle': 'Kuka näkee',
  'r3.notice.who': 'Koulun ylläpitäjä ja rehtori näkevät nimesi ja roolisi. Tapahtumaloki näkyy rehtorille ja ylläpitäjälle. Zenceran tuki näkee sen vain tukitilassa, joka näkyy koulun lokissa.',
  'r3.notice.studentsTitle': 'Oppilaiden vastaukset',
  'r3.notice.students': 'Oppilaat vastaavat nimettömästi. Näet oman luokkasi tuloksen vain, kun vähintään viisi vastasi. Kukaan, et sinäkään, ei näe yksittäistä oppilasta.',
  'r3.notice.checkinTitle': 'Oma kyselysi (vapaaehtoinen)',
  'r3.notice.checkin': 'Voit arvioida omaa kuormitustasi ja jaksamistasi. Vastauksesi näkyvät vain sinulle, ja se vaatii erillisen suostumuksen. Kuukauden ensimmäinen vastauksesi liittyy nimettömään koulun kuvaan, jonka rehtori näkee vain, jos vähintään viisi kollegaa osallistui.',
  'r3.notice.removeTitle': 'Tietojen poistaminen',
  'r3.notice.remove': 'Pyydä koulun ylläpitäjää poistamaan sinut koulusta tai Zenceraa poistamaan tilisi. Poisto poistaa henkilötietosi; lokiin jää vain tieto, että poistettu henkilö teki jotain.',
  'r3.notice.keepTitle': 'Kuinka kauan tietoja säilytetään',
  'r3.notice.keep': 'Tilisi niin kauan kuin työskentelet koulussa. Omat kyselysi 13 kuukautta tai kunnes perut suostumuksen. Tapahtumaloki koulun kanssa sovitun ajan.',
  'r3.notice.more': 'Lisätietoja:',
  'r3.notice.trustLink': 'Luottamus- ja vaatimustenmukaisuusasiakirjat',
  'r3.notice.accept': 'Olen lukenut tämän',
  'r3.me.nav': 'Oma kysely',
  'r3.me.title': 'Oma kysely',
  'r3.me.consentTitle': 'Oma kyselysi',
  'r3.me.c1': 'Neljä kysymystä asteikolla 1–5: kuormitus, energia, uni ja stressi. Vie minuutin.',
  'r3.me.c2': 'Vastauksesi ovat yksityisiä: vain sinä näet ne. Ei rehtori, ei ylläpitäjä, ei Zencera.',
  'r3.me.c3': 'Kuukauden ensimmäinen vastauksesi liittyy myös nimettömään koulun kuvaan, joka tallennetaan ilman nimeäsi ja aikaa. Rehtori näkee sen vain, jos vähintään viisi kollegaa osallistui; rehtoreiden omat vastaukset eivät ole siinä koskaan.',
  'r3.me.c4': 'Voit perua milloin tahansa: historiasi poistetaan heti. Jo lisättyjä nimettömiä kuukausisummia ei voi yhdistää sinuun, ja ne säilyvät.',
  'r3.me.agree': 'Hyväksyn oman kyselyn näillä ehdoilla.',
  'r3.me.consent': 'Aloita oma kysely',
  'r3.me.consentGiven': 'Suostumus tallennettu. Voit vastata nyt.',
  'r3.me.private': 'Vain sinä näet nämä vastaukset.',
  'r3.me.counted': 'Tämän kuukauden nimettömässä koulun kuvassa olet jo mukana.',
  'r3.me.notCounted': 'Tämän kuukauden ensimmäinen vastauksesi liittyy nimettömään koulun kuvaan.',
  'r3.me.todayTitle': 'Miten voit tänään?',
  'r3.me.todayDone': 'Tämän päivän vastaus (voit muuttaa sitä)',
  'r3.me.save': 'Tallenna',
  'r3.me.saved': 'Tallennettu.',
  'r3.me.history': 'Viimeisimmät vastauksesi',
  'r3.me.noHistory': 'Ei vielä mitään.',
  'r3.me.day': 'Päivä',
  'r3.me.keep': 'Säilytetään 13 kuukautta, sitten poistetaan.',
  'r3.me.withdraw': 'Peru suostumus ja poista historiani',
  'r3.me.withdrawTitle': 'Perutaanko suostumus?',
  'r3.me.withdrawBody': 'Vastaushistoriasi poistetaan nyt, eikä sitä voi palauttaa. Jo lisätyt nimettömät kuukausisummat säilyvät, koska niitä ei voi yhdistää sinuun.',
  'r3.me.withdrawConfirm': 'Peru ja poista',
  'r3.me.withdrawn': 'Suostumus peruttu. Historiasi on poistettu.',
  'r3.dim.workload': 'Kuormitus',
  'r3.dim.energy': 'Energia',
  'r3.dim.sleep': 'Uni',
  'r3.dim.stress': 'Stressi',
  'r3.dimHint.workload': '1 = hyvin kevyt, 5 = aivan liikaa',
  'r3.dimHint.energy': '1 = uupunut, 5 = täynnä energiaa',
  'r3.dimHint.sleep': '1 = hyvin huonosti, 5 = hyvin hyvin',
  'r3.dimHint.stress': '1 = rauhallinen, 5 = hyvin stressaantunut',
  'r3.me.pickedOf': (v: number) => `${v}/5`,
  'r3.sv.title': 'Henkilöstö ja oppilaat',
  'r3.sv.intro': (f) => `Kuukausittain rinnakkain: oppilaiden mieliala ja henkilöstön omat nimettömät vastaukset. Henkilöstön luku näytetään vain, jos vähintään ${f} kollegaa vastasi eivätkä lähes kaikki samoin. Rehtoreiden omia vastauksia ei ole mukana.`,
  'r3.sv.tableTitle': 'Päättyneet kuukaudet',
  'r3.sv.month': 'Kuukausi',
  'r3.sv.students': 'Oppilaiden mieliala',
  'r3.sv.scale': 'Henkilöstön asteikot: kuormitus ja stressi – suurempi on raskaampi; energia ja uni – suurempi on parempi.',
  'r3.sv.never': 'Ei koskaan henkilöittäin, tiimeittäin tai aineittain. Kuluva kuukausi näkyy, kun se on päättynyt.',
  'r3.sv.wholeOnly': 'Henkilöstönäkymä on koko koulun rehtoreille.',
  'r3.ww.title': 'Mikä toimii',
  'r3.ww.term': (term) => `Mikä toimii · ${term}`,
  'r3.ww.how': (s, a, r) => `Toimintakirjan toiminnot ja miten luokkien seuraava kysely vertautui. Näytetään vain, kun mukana on vähintään ${s} koulua, ${a} kokeilukertaa, ${r} vastausta ennen ja jälkeen, eikä mikään koulu ole yli puolta.`,
  'r3.ww.none': 'Millään toiminnolla ei ollut tällä lukukaudella tarpeeksi kouluja ja vastauksia.',
  'r3.ww.notYet': 'Ei vielä laskettu. Zencera laskee sen lukukauden päätyttyä.',
  'r3.ww.country': 'Suomi',
  'r3.ww.municipality': (m) => `Kunta ${m}`,
  'r3.ww.activity': 'Toiminto',
  'r3.ww.schools': 'Kouluja',
  'r3.ww.tried': 'Kokeiltu',
  'r3.ww.mood': 'Mieliala ennen → seuraava',
  'r3.ww.compare': 'Kun mitään ei kokeiltu',
  'r3.ww.topic': 'Aiheen osuus',
  'r3.ww.pts': (v) => `${v} prosenttiyksikköä`,
  'r3.ww.compareHint': 'Vertailukohtana ovat luokat, joilla oli sama aihe eikä toimintaa kirjattu.',
  'r3.ww.takesPart': 'Koulusi on mukana koulujen välisissä tilastoissa.',
  'r3.ww.notTakingPart': 'Koulusi ei ole vielä mukana koulujen välisissä tilastoissa. Näet silti tulokset.',
  'r3.cal.title': 'Koulun kalenteri',
  'r3.cal.intro': 'Koeviikot, lomat ja tapahtumat auttavat selittämään tulosten muutoksia ja suunnittelemaan kyselyjä. Opettajat näkevät kalenterin; rehtori ja ylläpitäjä muokkaavat sitä.',
  'r3.cal.add': 'Lisää kalenteriin',
  'r3.cal.edit': 'Muuta tapahtumaa',
  'r3.cal.kind': 'Laji',
  'r3.cal.k.exam_week': 'Koeviikko',
  'r3.cal.k.holiday': 'Loma',
  'r3.cal.k.event': 'Tapahtuma',
  'r3.cal.k.other': 'Muu',
  'r3.cal.what': 'Mikä se on',
  'r3.cal.noNames': 'Kuvaa viikkoa, älä ihmisiä: ei nimiä.',
  'r3.cal.from': 'Alkaen',
  'r3.cal.to': 'Päättyen',
  'r3.cal.gradesAll': 'Vuosiluokat (ei valintaa = koko koulu)',
  'r3.cal.gradesYours': 'Omat vuosiluokkasi',
  'r3.cal.coming': 'Kalenterissa',
  'r3.cal.upcoming': 'Tulossa koulussa',
  'r3.cal.none': 'Kalenterissa ei ole mitään.',
  'r3.cal.wholeSchool': 'Koko koulu',
  'r3.cal.editBtn': 'Muuta',
  'r3.cal.deleteBtn': 'Poista',
  'r3.cal.deleted': 'Poistettu.',
  'r3.sig.title': 'Varhaiset signaalit',
  'r3.sig.none': 'Mikään aihe ei ole nousussa.',
  'r3.sig.rose': (s) => `nousi kolme viikkoa: ${s} % vastauksista`,
  'r3.sig.events': 'Kalenterissa noilla viikoilla',
  'r3.sig.how': 'Signaali on aihe, jonka osuus nousi kunkin kolmen viimeisen päättyneen viikon aikana yhteensä vähintään 10 prosenttiyksikköä, vain näytetyistä viikoista laskettuna.',
  'r3.notif.signal': (topic) => `Varhainen signaali: ”${topic}” on noussut kolme viikkoa. Avaa koulun tulokset.`,
  'r3.thl.title': 'Vertailu THL:n Kouluterveyskyselyyn',
  'r3.thl.intro': 'Kutakin aihettamme lähin THL:n kansallisen kyselyn indikaattori sekä viitearvot, jotka Zencera syöttää THL:n julkaisemista taulukoista.',
  'r3.thl.mood': 'Mieliala',
  'r3.thl.noValue': 'viitearvoa ei ole vielä syötetty',
  'r3.thl.note': 'Kysymykset eroavat, joten luvut ovat taustaksi, eivät suora vertailu.',
  'r3.thlStage.4-5': 'Vuosiluokat 4–5',
  'r3.thlStage.8-9': 'Vuosiluokat 8–9',
  'r3.thlStage.lukio': 'Lukio',
  'r3.thlStage.amm': 'Ammatillinen',
  'r3.mu.title': 'Kunnan hyvinvointikuva',
  'r3.mu.nav': 'Kunta',
  'r3.mu.intro': (m, a, min, per) => `${m}: ${n(a, '1 koulu on', `${a} koulua on`)} suostunut osallistumaan. Neljännes näytetään, kun vähintään ${min} niistä sai ${per} tai enemmän vastauksia, eikä koskaan niin, että yksi koulu paljastuisi.`,
  'r3.mu.quarters': 'Päättyneet vuosineljännekset',
  'r3.mu.quarter': 'Neljännes',
  'r3.mu.all': 'Kaikki luokka-asteet',
  'r3.mu.frozen': 'Neljännes lasketaan kerran sen päätyttyä, eikä se muutu sen jälkeen.',
  'r3.mu.topics': (p) => `Mikä oppilaita mietitytti · ${p}`,
  'r3.xs.title': 'Koulujen väliset tilastot',
  'r3.xs.hint': 'Koulun luvut liitetään kunnallisiin ja kansallisiin tilastoihin vasta, kun koulu on antanut siihen kirjallisen luvan. Vain käytössä olevat asiakaskoulut lasketaan. Peruminen lopettaa laskemisen kaikessa myöhemmin lasketussa.',
  'r3.xs.authorised': (on, who) => `Lupa ${on} · ${who}`,
  'r3.xs.notAuthorised': 'Ei lupaa',
  'r3.xs.notCounting': 'Ei lasketa nyt: vain käytössä olevat asiakaskoulut lasketaan.',
  'r3.xs.withdraw': 'Kirjaa peruminen',
  'r3.ai.nav': 'Oivallukset',
  'r3.ai.title': 'Koulujen väliset oivallukset',
  'r3.ai.intro': (a, c, m) => `${n(a, '1 koulu on', `${a} koulua on`)} mukana. Zencera näkee vain yhdistetyt tulokset: vähintään ${c} koulua Suomen tasolla ja ${m} kunnan tasolla. Ei koskaan yksittäistä koulua.`,
  'r3.ai.byMuni': 'Mukana kunnittain',
  'r3.ai.noneYet': 'Yksikään koulu ei ole vielä antanut lupaa.',
  'r3.ai.coordN': (c) => n(c, '1 koordinaattori', `${c} koordinaattoria`),
  'r3.ai.coordTitle': 'Kuntien koordinaattorit',
  'r3.ai.coordHint': 'Koordinaattori näkee vain kuntansa neljännesvuosiraportin. Kirjautuminen kaksivaiheisella tunnistautumisella.',
  'r3.ai.email': 'Olemassa olevan tilin sähköposti',
  'r3.ai.muni': 'Kunta',
  'r3.ai.choose': 'Valitse…',
  'r3.ai.grant': 'Nimeä koordinaattoriksi',
  'r3.ai.revoke': 'Poista',
  'r3.ai.revoked': 'Koordinaattori poistettu.',
  'r3.ai.reportsTitle': 'Kausiraportit',
  'r3.ai.reportsHint': 'Esikatsele päättynyt neljännes Suomen tai kunnan tasolla. Julkaistut raportit ovat julkisia ja merkitty luonnokseksi.',
  'r3.ai.level': 'Alue',
  'r3.ai.preview': 'Esikatsele',
  'r3.ai.repTitle': 'Otsikko',
  'r3.ai.repComment': 'Kommentti (valinnainen)',
  'r3.ai.publishHint': 'Julkaisu estetään, jos lukija voisi vähentää sen toisesta julkaistusta raportista ja nähdä pienen ryhmän.',
  'r3.ai.publish': 'Julkaise',
  'r3.ai.published': 'Julkaistu.',
  'r3.ai.publishedList': 'Julkaistut',
  'r3.ai.public': 'Julkinen',
  'r3.ai.withdrawn': 'Peruttu',
  'r3.ai.withdraw': 'Peru',
  'r3.ai.wwTitle': '”Mikä toimii” -laskennat',
  'r3.ai.wwHint': 'Suorita kerran kunkin päättyneen lukukauden jälkeen. Tulos jäädytetään; koulukohtaista tietoa ei säilytetä.',
  'r3.ai.computedTerms': (s) => `Laskettu: ${s}`,
  'r3.ai.term': 'Lukukausi',
  'r3.ai.termHint': 'Esimerkiksi 2026-spring tai 2026-autumn.',
  'r3.ai.compute': 'Laske',
  'r3.ai.computed': (c) => `Laskettu: ${n(c, '1 rivi', `${c} riviä`)} ylitti rajat.`,
  'r3.ai.thlHint': 'Syötä vain THL:n julkaisemien taulukoiden arvoja ja merkitse taulukko lähteeksi.',
  'r3.ai.indicator': 'Indikaattori',
  'r3.ai.stage': 'Luokka-asteet',
  'r3.ai.year': 'Vuosi',
  'r3.ai.value': 'Arvo (%)',
  'r3.ai.source': 'Lähde (THL:n taulukko)',
  'r3.qv.title': 'Kysymyssarjojen validointi',
  'r3.qv.hint': 'Näkyy julkisella menetelmäsivulla. Merkitse validoiduksi vasta tutkimuskumppanin arvion jälkeen.',
  'r3.qv.note': 'Huomautus',
  'r3.qv.draft': 'Luonnos – ei validoitu',
  'r3.qv.pilot_tested': 'Pilotoitu',
  'r3.qv.validated': 'Validoitu',
  'r3.method.title': 'Miten Swipe suojaa yksityisyyttä',
  'r3.method.intro': 'Alla olevat säännöt ovat samat, joita tietokanta valvoo. Sivun luvut luetaan suoraan siitä.',
  'r3.method.failed': 'Lukuja ei voitu ladata. Yritä myöhemmin uudelleen.',
  'r3.method.studentsTitle': 'Oppilaat',
  'r3.method.noId': 'Oppilaat vastaavat ilman nimiä, tilejä tai laitetunnisteita. Ei vapaata tekstiä.',
  'r3.method.floor': (f) => `Tulos näytetään vain, kun vähintään ${f} oppilasta vastasi.`,
  'r3.method.guard': 'Jos kaikki yhtä lukuun ottamatta vastasivat samoin, tulos piilotetaan, jottei se yksi paljastu.',
  'r3.method.complement': 'Kun kaksi näytettyä tulosta voitaisiin vähentää toisistaan, myös erotuksen on oltava riittävän suuri; muuten toinen piilotetaan.',
  'r3.method.weeks': 'Rehtorit näkevät vain päättyneet viikot; opettaja näkee oman luokkansa kustakin päättyneestä kyselystä.',
  'r3.method.council': (f) => `Oppilaskunnan linkki näyttää koulutason viikot, joissa on vähintään ${f} vastausta.`,
  'r3.method.staffTitle': 'Henkilöstö',
  'r3.method.staffPrivate': 'Henkilöstön jäsenen oma kysely on vain hänen nähtävissään ja vaatii hänen suostumuksensa.',
  'r3.method.staffAgg': (f) => `Nimetön kuukausittainen henkilöstön kuva tallennetaan ilman nimiä ja aikoja, ja rehtori näkee sen vain, kun mukana on vähintään ${f} kollegaa.`,
  'r3.method.staffHeads': 'Rehtoreiden omat vastaukset eivät koskaan ole rehtorin näkemässä kuvassa.',
  'r3.method.xsTitle': 'Koulujen välillä',
  'r3.method.xsAuth': 'Koulu lasketaan mukaan vasta, kun se on antanut kirjallisen luvan.',
  'r3.method.xsFloors': (m, c, a) => `Kunta tarvitsee vähintään ${m} koulua ja Suomi ${c}, kukin vähintään ${a} vastauksella; minkä tahansa yhden koulun pois jättäminen ei saa paljastaa sitä.`,
  'r3.method.xsFrozen': 'Neljännekset ja lukukaudet lasketaan kerran päättymisen jälkeen, eivätkä ne muutu, joten kahta lukukertaa ei voi verrata koulun paljastamiseksi.',
  'r3.method.ww': (a, r) => `”Mikä toimii” vaatii ${a} kokeilukertaa ja ${r} vastausta ennen ja jälkeen, eikä mikään koulu saa olla yli puolta.`,
  'r3.method.zencera': 'Zencera ei koskaan näe yksittäisen koulun tuloksia – vain nämä yhdistetyt luvut.',
  'r3.method.questionsTitle': 'Kysymykset',
  'r3.pub.title': 'Julkaistut hyvinvointiraportit',
  'r3.pub.intro': 'Yhdistetyt tulokset kouluista, jotka ovat suostuneet osallistumaan. Luonnos, kunnes menetelmä on arvioitu ulkopuolisesti.',
  'r3.pub.none': 'Raportteja ei ole vielä julkaistu.',
  'fs.what_works': 'Mikä toimii (koulujen välinen)',
  'fs.municipality_reports': 'Kuntaraportit',
  'fs.period_reports': 'Zenceran kausiraportit',
  'fs.school_calendar': 'Koulun kalenteri',
  'fs.early_signals': 'Varhaiset signaalit',
  'fs.staff_checkin': 'Henkilöstön oma kysely',
  'fs.staff_view': 'Rehtorin henkilöstönäkymä',
  'fs.thl_alignment': 'THL-vertailu',
  'err.premium_required': 'Tämä kuuluu laajennettuun oivallukset-lisäpalveluun.',
  'err.consent_required': 'Anna ensin suostumuksesi.',
  'err.outside_scope': 'Voit tehdä tämän vain omille vuosiluokillesi.',
  'err.whole_school_only': 'Tämä on koko koulun rehtoreille.',
  'err.period_not_complete': 'Vain päättyneet jaksot voidaan näyttää.',
  'err.already_computed': 'Tämä lukukausi on laskettu ja jäädytetty.',
  'err.already_published': 'Raportti on jo julkaistu.',
  'err.below_floor': 'Liian vähän kouluja tai vastauksia julkaistavaksi.',
  'err.complement_too_small': 'Julkaisu paljastaisi pienen ryhmän vähennyslaskulla.',
  'err.already_coordinator': 'Henkilö on jo koordinaattori.',
  'err.stale_notice': 'Ilmoitus on muuttunut. Lataa sivu uudelleen.',
  'err.not_found': 'Ei löytynyt.',
  'err.unknown_account': 'Tällä sähköpostilla ei ole aktiivista tiliä.',
};

export const r3Sv: R3Strings = {
  'r3.role.coordinator': 'Kommunkoordinator',
  'r3.role.coordinatorDesc': 'Ser kommunens kvartalsvisa välmåendebild, sammanslagen för de skolor som gått med. Aldrig en enskild skola eller klass.',
  'r3.nav.whatWorks': 'Vad fungerar',
  'r3.nav.staff': 'Personal och elever',
  'r3.nav.calendar': 'Kalender',
  'r3.schoolsN': (c) => n(c, '1 skola', `${c} skolor`),
  'r3.tooFewSchools': (c) => `För få skolor (${c})`,
  'r3.st.ok': 'Visas',
  'r3.st.too_few_schools': 'För få skolor',
  'r3.st.insufficient': 'För få svar för att visas',
  'r3.st.insufficient_spread': 'Dold – en skola skulle synas',
  'r3.st.suppressed_near_unanimous': 'Dold – nästan alla lika',
  'r3.st.hidden_complement': 'Dold tillsammans med ett annat stadium',
  'r3.stage.primary': 'Årskurs 1–6',
  'r3.stage.lower_secondary': 'Årskurs 7–9',
  'r3.stage.upper_secondary': 'Andra stadiet',
  'r3.term.spring': (y) => `Vårterminen ${y}`,
  'r3.term.autumn': (y) => `Hösterminen ${y}`,
  'r3.notCausal': 'Detta visar vad som hände därefter, inte att aktiviteten orsakade det.',
  'r3.evidence': (band, n2, mood) => `I ${band} skolor (prövat ${n2} gånger) ändrades stämningen i nästa enkät med ${mood}.`,
  'r3.evidenceBase': (b) => `Där inget prövades: ${b}.`,
  'r3.premiumWw': '”Vad fungerar” ingår i tillvalet för utökade insikter. Be Zencera aktivera det för din skola.',
  'r3.premiumSignals': 'Tidiga signaler ingår i tillvalet för utökade insikter.',
  'r3.notice.title': 'Innan du börjar: dina uppgifter',
  'r3.notice.intro': 'Läs detta en gång. Här står vad Swipe sparar om dig som anställd, vem som ser vad och hur uppgifterna tas bort.',
  'r3.notice.storedTitle': 'Vad vi sparar om dig',
  'r3.notice.stored': 'Ditt namn, din e-postadress och dina roller i varje skola; när du loggar in; och en logg över ändringar du gör (till exempel att starta en enkät eller godkänna behörighet). Inga betyg och ingen bedömning av ditt arbete.',
  'r3.notice.whoTitle': 'Vem ser det',
  'r3.notice.who': 'Skolans administratör och rektor ser ditt namn och dina roller. Händelseloggen syns för rektorn och administratören. Zenceras support ser den bara i supportläge, vilket syns i skolans logg.',
  'r3.notice.studentsTitle': 'Elevernas svar',
  'r3.notice.students': 'Eleverna svarar anonymt. Du ser din egen klass bara när minst fem svarat. Ingen, inte heller du, ser en enskild elev.',
  'r3.notice.checkinTitle': 'Din egen enkät (frivillig)',
  'r3.notice.checkin': 'Du kan bedöma din egen arbetsbörda och ork. Svaren syns bara för dig och kräver ett separat samtycke. Ditt första svar varje månad ingår i en anonym bild av skolan, som rektorn ser bara när minst fem kolleger deltagit.',
  'r3.notice.removeTitle': 'Att få uppgifterna borttagna',
  'r3.notice.remove': 'Be skolans administratör ta bort dig från skolan, eller be Zencera radera ditt konto. Raderingen tar bort dina personuppgifter; loggen behåller bara att en raderad person gjort något.',
  'r3.notice.keepTitle': 'Hur länge vi sparar',
  'r3.notice.keep': 'Ditt konto så länge du arbetar vid skolan. Dina egna enkätsvar i 13 månader eller tills du återkallar samtycket. Händelseloggen den tid som avtalats med skolan.',
  'r3.notice.more': 'Mer information:',
  'r3.notice.trustLink': 'Förtroende- och efterlevnadsdokument',
  'r3.notice.accept': 'Jag har läst detta',
  'r3.me.nav': 'Min enkät',
  'r3.me.title': 'Min enkät',
  'r3.me.consentTitle': 'Din egen enkät',
  'r3.me.c1': 'Fyra frågor på skalan 1–5: arbetsbörda, energi, sömn och stress. Tar en minut.',
  'r3.me.c2': 'Dina svar är privata: bara du ser dem. Inte rektorn, inte administratören, inte Zencera.',
  'r3.me.c3': 'Ditt första svar varje månad ingår också i en anonym bild av skolan, sparad utan ditt namn och utan tid. Rektorn ser den bara när minst fem kolleger deltagit; rektorernas egna svar ingår aldrig.',
  'r3.me.c4': 'Du kan återkalla när som helst: din historik raderas genast. Anonyma månadssummor som redan lagts till kan inte kopplas till dig och finns kvar.',
  'r3.me.agree': 'Jag samtycker till min egen enkät på dessa villkor.',
  'r3.me.consent': 'Börja min enkät',
  'r3.me.consentGiven': 'Samtycket är registrerat. Du kan svara nu.',
  'r3.me.private': 'Bara du ser dessa svar.',
  'r3.me.counted': 'Du ingår redan i månadens anonyma bild av skolan.',
  'r3.me.notCounted': 'Ditt första svar denna månad ingår i den anonyma bilden av skolan.',
  'r3.me.todayTitle': 'Hur mår du i dag?',
  'r3.me.todayDone': 'Dagens svar (du kan ändra det)',
  'r3.me.save': 'Spara',
  'r3.me.saved': 'Sparat.',
  'r3.me.history': 'Dina senaste svar',
  'r3.me.noHistory': 'Inget ännu.',
  'r3.me.day': 'Dag',
  'r3.me.keep': 'Sparas i 13 månader och raderas sedan.',
  'r3.me.withdraw': 'Återkalla samtycket och radera min historik',
  'r3.me.withdrawTitle': 'Återkalla samtycket?',
  'r3.me.withdrawBody': 'Din svarshistorik raderas nu och kan inte återställas. Anonyma månadssummor som redan lagts till finns kvar, eftersom de inte kan kopplas till dig.',
  'r3.me.withdrawConfirm': 'Återkalla och radera',
  'r3.me.withdrawn': 'Samtycket är återkallat. Din historik är raderad.',
  'r3.dim.workload': 'Arbetsbörda',
  'r3.dim.energy': 'Energi',
  'r3.dim.sleep': 'Sömn',
  'r3.dim.stress': 'Stress',
  'r3.dimHint.workload': '1 = mycket lätt, 5 = alldeles för mycket',
  'r3.dimHint.energy': '1 = utmattad, 5 = full av energi',
  'r3.dimHint.sleep': '1 = mycket dåligt, 5 = mycket bra',
  'r3.dimHint.stress': '1 = lugn, 5 = mycket stressad',
  'r3.me.pickedOf': (v: number) => `${v} av 5`,
  'r3.sv.title': 'Personal och elever',
  'r3.sv.intro': (f) => `Månadsvis sida vid sida: elevernas stämning och personalens egna anonyma svar. En personalsiffra visas bara när minst ${f} kolleger svarat och inte nästan alla lika. Rektorernas egna svar ingår aldrig.`,
  'r3.sv.tableTitle': 'Avslutade månader',
  'r3.sv.month': 'Månad',
  'r3.sv.students': 'Elevernas stämning',
  'r3.sv.scale': 'Personalens skalor: arbetsbörda och stress – högre är tyngre; energi och sömn – högre är bättre.',
  'r3.sv.never': 'Aldrig per person, team eller ämne. Innevarande månad visas när den är slut.',
  'r3.sv.wholeOnly': 'Personalvyn är för rektorer för hela skolan.',
  'r3.ww.title': 'Vad fungerar',
  'r3.ww.term': (term) => `Vad fungerar · ${term}`,
  'r3.ww.how': (s, a, r) => `Aktiviteter ur handboken och hur klassernas nästa enkät jämfördes. Visas bara med minst ${s} skolor, ${a} försök, ${r} svar före och efter, och ingen skola mer än hälften.`,
  'r3.ww.none': 'Ingen aktivitet hade tillräckligt många skolor och svar denna termin.',
  'r3.ww.notYet': 'Inte beräknat ännu. Zencera beräknar när terminen är slut.',
  'r3.ww.country': 'Finland',
  'r3.ww.municipality': (m) => `Kommun ${m}`,
  'r3.ww.activity': 'Aktivitet',
  'r3.ww.schools': 'Skolor',
  'r3.ww.tried': 'Prövat',
  'r3.ww.mood': 'Stämning före → nästa',
  'r3.ww.compare': 'Där inget prövades',
  'r3.ww.topic': 'Ämnets andel',
  'r3.ww.pts': (v) => `${v} procentenheter`,
  'r3.ww.compareHint': 'Jämförelsen är klasser som hade samma ämne och inte registrerade någon aktivitet.',
  'r3.ww.takesPart': 'Din skola deltar i statistik över skolorna.',
  'r3.ww.notTakingPart': 'Din skola deltar inte ännu i statistik över skolorna. Du ser ändå resultaten.',
  'r3.cal.title': 'Skolans kalender',
  'r3.cal.intro': 'Provveckor, lov och evenemang hjälper till att förklara förändringar i resultaten och att planera enkäter. Lärarna ser kalendern; rektorn och administratören redigerar den.',
  'r3.cal.add': 'Lägg till i kalendern',
  'r3.cal.edit': 'Ändra händelsen',
  'r3.cal.kind': 'Typ',
  'r3.cal.k.exam_week': 'Provvecka',
  'r3.cal.k.holiday': 'Lov',
  'r3.cal.k.event': 'Evenemang',
  'r3.cal.k.other': 'Annat',
  'r3.cal.what': 'Vad det är',
  'r3.cal.noNames': 'Beskriv veckan, inte personer: inga namn.',
  'r3.cal.from': 'Från',
  'r3.cal.to': 'Till',
  'r3.cal.gradesAll': 'Årskurser (inget valt = hela skolan)',
  'r3.cal.gradesYours': 'Dina årskurser',
  'r3.cal.coming': 'I kalendern',
  'r3.cal.upcoming': 'På gång i skolan',
  'r3.cal.none': 'Inget i kalendern.',
  'r3.cal.wholeSchool': 'Hela skolan',
  'r3.cal.editBtn': 'Ändra',
  'r3.cal.deleteBtn': 'Radera',
  'r3.cal.deleted': 'Raderat.',
  'r3.sig.title': 'Tidiga signaler',
  'r3.sig.none': 'Inget ämne ökar.',
  'r3.sig.rose': (s) => `har ökat i tre veckor: ${s} % av svaren`,
  'r3.sig.events': 'I kalendern de veckorna',
  'r3.sig.how': 'En signal är ett ämne vars andel ökat under var och en av de tre senaste avslutade veckorna, med minst 10 procentenheter totalt, räknat bara på veckor som visats.',
  'r3.notif.signal': (topic) => `Tidig signal: ”${topic}” har ökat i tre veckor. Öppna skolans resultat.`,
  'r3.thl.title': 'Jämfört med THL:s enkät Hälsa i skolan',
  'r3.thl.intro': 'Närmaste indikator i THL:s nationella enkät för vart och ett av våra ämnen, med referensvärden som Zencera för in från THL:s publicerade tabeller.',
  'r3.thl.mood': 'Stämning',
  'r3.thl.noValue': 'inget referensvärde inmatat ännu',
  'r3.thl.note': 'Frågorna skiljer sig, så siffrorna är bakgrund, inte en direkt jämförelse.',
  'r3.thlStage.4-5': 'Årskurs 4–5',
  'r3.thlStage.8-9': 'Årskurs 8–9',
  'r3.thlStage.lukio': 'Gymnasiet',
  'r3.thlStage.amm': 'Yrkesutbildning',
  'r3.mu.title': 'Kommunens välmåendebild',
  'r3.mu.nav': 'Kommun',
  'r3.mu.intro': (m, a, min, per) => `${m}: ${n(a, '1 skola har', `${a} skolor har`)} gått med på att delta. Ett kvartal visas när minst ${min} av dem fått ${per} svar eller fler, och aldrig så att en skola avslöjas.`,
  'r3.mu.quarters': 'Avslutade kvartal',
  'r3.mu.quarter': 'Kvartal',
  'r3.mu.all': 'Alla årskurser',
  'r3.mu.frozen': 'Ett kvartal beräknas en gång när det är slut och ändras inte därefter.',
  'r3.mu.topics': (p) => `Vad eleverna funderade på · ${p}`,
  'r3.xs.title': 'Statistik över skolorna',
  'r3.xs.hint': 'Skolans siffror ingår i kommunal och nationell statistik först när skolan gett skriftligt tillstånd. Bara kundskolor i drift räknas. Återkallelse gör att skolan inte räknas i något som beräknas därefter.',
  'r3.xs.authorised': (on, who) => `Tillstånd ${on} · ${who}`,
  'r3.xs.notAuthorised': 'Inget tillstånd',
  'r3.xs.notCounting': 'Räknas inte nu: bara kundskolor i drift räknas.',
  'r3.xs.withdraw': 'Registrera återkallelse',
  'r3.ai.nav': 'Insikter',
  'r3.ai.title': 'Insikter över skolorna',
  'r3.ai.intro': (a, c, m) => `${n(a, '1 skola deltar', `${a} skolor deltar`)}. Zencera ser bara sammanslagna resultat: minst ${c} skolor för Finland och ${m} för en kommun. Aldrig en enskild skola.`,
  'r3.ai.byMuni': 'Deltar, per kommun',
  'r3.ai.noneYet': 'Ingen skola har gett tillstånd ännu.',
  'r3.ai.coordN': (c) => n(c, '1 koordinator', `${c} koordinatorer`),
  'r3.ai.coordTitle': 'Kommunkoordinatorer',
  'r3.ai.coordHint': 'En koordinator ser bara sin kommuns kvartalsrapport. Inloggning med tvåstegsverifiering.',
  'r3.ai.email': 'E-post för ett befintligt konto',
  'r3.ai.muni': 'Kommun',
  'r3.ai.choose': 'Välj…',
  'r3.ai.grant': 'Utse till koordinator',
  'r3.ai.revoke': 'Ta bort',
  'r3.ai.revoked': 'Koordinatorn är borttagen.',
  'r3.ai.reportsTitle': 'Periodrapporter',
  'r3.ai.reportsHint': 'Förhandsgranska ett avslutat kvartal för Finland eller en kommun. Publicerade rapporter är offentliga och märkta som utkast.',
  'r3.ai.level': 'Område',
  'r3.ai.preview': 'Förhandsgranska',
  'r3.ai.repTitle': 'Rubrik',
  'r3.ai.repComment': 'Kommentar (valfri)',
  'r3.ai.publishHint': 'Publiceringen nekas om en läsare kunde dra av den från en annan publicerad rapport och se en liten grupp.',
  'r3.ai.publish': 'Publicera',
  'r3.ai.published': 'Publicerad.',
  'r3.ai.publishedList': 'Publicerade',
  'r3.ai.public': 'Offentlig',
  'r3.ai.withdrawn': 'Återkallad',
  'r3.ai.withdraw': 'Återkalla',
  'r3.ai.wwTitle': 'Beräkningar av ”Vad fungerar”',
  'r3.ai.wwHint': 'Kör en gång för varje avslutad termin. Resultatet fryses; inget per skola sparas.',
  'r3.ai.computedTerms': (s) => `Beräknat: ${s}`,
  'r3.ai.term': 'Termin',
  'r3.ai.termHint': 'Till exempel 2026-spring eller 2026-autumn.',
  'r3.ai.compute': 'Beräkna',
  'r3.ai.computed': (c) => `Beräknat: ${n(c, '1 rad', `${c} rader`)} klarade gränserna.`,
  'r3.ai.thlHint': 'För bara in värden från THL:s publicerade tabeller och ange tabellen som källa.',
  'r3.ai.indicator': 'Indikator',
  'r3.ai.stage': 'Årskurser',
  'r3.ai.year': 'År',
  'r3.ai.value': 'Värde (%)',
  'r3.ai.source': 'Källa (THL:s tabell)',
  'r3.qv.title': 'Validering av frågeuppsättningar',
  'r3.qv.hint': 'Visas på den offentliga metodsidan. Markera som validerad först efter granskning av en forskningspartner.',
  'r3.qv.note': 'Anteckning',
  'r3.qv.draft': 'Utkast – inte validerad',
  'r3.qv.pilot_tested': 'Pilottestad',
  'r3.qv.validated': 'Validerad',
  'r3.method.title': 'Hur Swipe skyddar integriteten',
  'r3.method.intro': 'Reglerna nedan är samma som databasen upprätthåller. Siffrorna på sidan läses därifrån.',
  'r3.method.failed': 'Siffrorna kunde inte laddas. Försök igen senare.',
  'r3.method.studentsTitle': 'Elever',
  'r3.method.noId': 'Eleverna svarar utan namn, konton eller enhetsidentifierare. Ingen fritext.',
  'r3.method.floor': (f) => `Ett resultat visas bara när minst ${f} elever svarat.`,
  'r3.method.guard': 'Om alla utom en svarat likadant döljs resultatet, så att den enda inte avslöjas.',
  'r3.method.complement': 'När två visade resultat kunde dras av från varandra måste även skillnaden vara tillräckligt stor; annars döljs det ena.',
  'r3.method.weeks': 'Rektorer ser bara avslutade veckor; en lärare ser sin egen klass per avslutad enkät.',
  'r3.method.council': (f) => `Elevkårens länk visar skolans veckor med minst ${f} svar.`,
  'r3.method.staffTitle': 'Personal',
  'r3.method.staffPrivate': 'En anställds egen enkät syns bara för personen själv och kräver samtycke.',
  'r3.method.staffAgg': (f) => `Den anonyma månatliga personalbilden sparas utan namn och tider och visas för rektorn bara med minst ${f} kolleger.`,
  'r3.method.staffHeads': 'Rektorernas egna svar ingår aldrig i bilden en rektor ser.',
  'r3.method.xsTitle': 'Över skolorna',
  'r3.method.xsAuth': 'En skola räknas först när den gett skriftligt tillstånd.',
  'r3.method.xsFloors': (m, c, a) => `En kommun behöver minst ${m} skolor och Finland ${c}, var och en med minst ${a} svar; att utelämna en enskild skola får inte avslöja den.`,
  'r3.method.xsFrozen': 'Kvartal och terminer beräknas en gång när de är slut och ändras inte, så två avläsningar kan inte jämföras för att avslöja en skola.',
  'r3.method.ww': (a, r) => `”Vad fungerar” kräver ${a} försök och ${r} svar före och efter, och ingen skola får stå för mer än hälften.`,
  'r3.method.zencera': 'Zencera ser aldrig en enskild skolas resultat – bara dessa sammanslagna siffror.',
  'r3.method.questionsTitle': 'Frågor',
  'r3.pub.title': 'Publicerade välmåenderapporter',
  'r3.pub.intro': 'Sammanslagna resultat för skolor som gått med på att delta. Utkast tills metoden granskats externt.',
  'r3.pub.none': 'Inga rapporter har publicerats ännu.',
  'fs.what_works': 'Vad fungerar (över skolorna)',
  'fs.municipality_reports': 'Kommunrapporter',
  'fs.period_reports': 'Zenceras periodrapporter',
  'fs.school_calendar': 'Skolans kalender',
  'fs.early_signals': 'Tidiga signaler',
  'fs.staff_checkin': 'Personalens egen enkät',
  'fs.staff_view': 'Rektorns personalvy',
  'fs.thl_alignment': 'Jämförelse med THL',
  'err.premium_required': 'Detta ingår i tillvalet för utökade insikter.',
  'err.consent_required': 'Ge först ditt samtycke.',
  'err.outside_scope': 'Du kan göra detta bara för dina egna årskurser.',
  'err.whole_school_only': 'Detta är för rektorer för hela skolan.',
  'err.period_not_complete': 'Bara avslutade perioder kan visas.',
  'err.already_computed': 'Terminen är beräknad och fryst.',
  'err.already_published': 'Rapporten är redan publicerad.',
  'err.below_floor': 'För få skolor eller svar för att publicera.',
  'err.complement_too_small': 'Publiceringen skulle låta en liten grupp räknas fram genom subtraktion.',
  'err.already_coordinator': 'Personen är redan koordinator.',
  'err.stale_notice': 'Meddelandet har ändrats. Ladda om sidan.',
  'err.not_found': 'Hittades inte.',
  'err.unknown_account': 'Det finns inget aktivt konto med den e-postadressen.',
};
